import type { ChunkTemplate } from './ChunkDefs';

/**
 * Authored chunk templates (MAPGEN_PLAN.md §2.2). Conventions:
 *   - 24×24 rows (size 1) or 48×48 (size 2, grand).
 *   - `#` wall · `.` floor · `e` event anchor · `*` room center (exactly 1).
 *   - Open edges carry the standard 8-wide aperture at cols/rows 8–15 of the
 *     segment, floor to depth ≥2; all other boundary tiles are wall.
 *   - Author in ONE canonical orientation — rotation/mirror expansion is free
 *     (ChunkDefs.buildVariantIndex gives every template 8 variants).
 *   - Walls are deliberately wobbled/asymmetric (Sarah 2026-07-23: no clean
 *     architectural look); the assembler's erosion pass adds per-seed noise
 *     on top, so templates only need coarse irregularity.
 *
 * Shape classes by open edges (canonical): entry/cap = N only; straight =
 * N+S; corner = N+E; T = N+E+W; cross = all. Validation lives in
 * ChunkDefs.validateTemplate; ChunkDefs.test.ts runs it over this library and
 * asserts every mask 1–15 is coverable.
 */
export const CHUNK_TEMPLATES: readonly ChunkTemplate[] = [
  // ---- entry (entrance chamber; cap-shaped, never used mid-floor) --------
  {
    id: 'entry-hollow',
    kind: 'entry',
    size: 1,
    rows: [
      '########........########',
      '######............######',
      '#####..............#####',
      '####................####',
      '###..................###',
      '###..................###',
      '##....................##',
      '##....................##',
      '##....................##',
      '##.........*..........##',
      '##....................##',
      '###..................###',
      '###..................###',
      '####................####',
      '#####..............#####',
      '######............######',
      '#######..........#######',
      '#########......#########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },

  // ---- caps (dead-end pocket rooms, branch ends) -------------------------
  {
    id: 'cap-den',
    kind: 'cap',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '######............######',
      '#####..............#####',
      '####........e.......####',
      '####................####',
      '###..................###',
      '###.......*..........###',
      '###..................###',
      '####................####',
      '####.....e..........####',
      '#####..............#####',
      '######............######',
      '#######..........#######',
      '#########......#########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    id: 'cap-cell',
    kind: 'cap',
    size: 1,
    rows: [
      '########........########',
      '######...........#######',
      '####..............######',
      '###................#####',
      '###.................####',
      '##...........e......####',
      '##..................####',
      '##........*.........####',
      '##..................####',
      '###.....e..........#####',
      '####..............######',
      '######...........#######',
      '#########......#########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },

  // ---- straights (open N+S) ----------------------------------------------
  {
    id: 'hall-straight',
    kind: 'hall',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '#######..........#######',
      '######...........#######',
      '######............######',
      '#####.............######',
      '#####..e..........######',
      '######............######',
      '######............######',
      '#######...........######',
      '#######....*......######',
      '#######...........######',
      '######............######',
      '######.............#####',
      '#####..............#####',
      '#####.............######',
      '######............######',
      '######...........#######',
      '#######..........#######',
      '#######..........#######',
      '#######..........#######',
      '########.........#######',
      '########..........######',
      '########........########',
    ],
  },
  {
    id: 'hall-kinked',
    kind: 'hall',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '######...........#######',
      '#####...........########',
      '####...........#########',
      '###...........##########',
      '###..........###########',
      '##...........###########',
      '##..........############',
      '##..........############',
      '##...*....e..###########',
      '##...........###########',
      '###...........##########',
      '####...........#########',
      '#####...........########',
      '######...........#######',
      '#######...........######',
      '########..........######',
      '########..........######',
      '########.........#######',
      '########.........#######',
      '########.........#######',
      '########.........#######',
      '########........########',
    ],
  },
  {
    id: 'room-way',
    kind: 'room',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '######............######',
      '#####..............#####',
      '####................####',
      '###...............e..###',
      '###...................##',
      '##....................##',
      '##.....e..............##',
      '##....................##',
      '##....................##',
      '##.........*..........##',
      '##....................##',
      '##....................##',
      '##..............e.....##',
      '###..................###',
      '###..................###',
      '####................####',
      '#####..............#####',
      '######............######',
      '######............######',
      '#######..........#######',
      '#######..........#######',
      '########........########',
    ],
  },

  // ---- corners (open N+E) ------------------------------------------------
  {
    id: 'hall-corner',
    kind: 'hall',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '#######..........#######',
      '######............######',
      '######............######',
      '######.............#####',
      '######..............####',
      '######...............###',
      '######..................',
      '######..................',
      '#######.................',
      '#######......*.....e....',
      '#######.................',
      '########................',
      '########................',
      '########................',
      '#########.........######',
      '##########......########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    id: 'room-corner',
    kind: 'room',
    size: 1,
    rows: [
      '########........########',
      '######............######',
      '#####..............#####',
      '####................####',
      '###..................###',
      '###...................##',
      '##....................##',
      '##...................###',
      '##......................',
      '##......................',
      '##.......*..............',
      '##..........e...........',
      '###.....................',
      '###.....................',
      '####....................',
      '####....................',
      '#####...............####',
      '######.............#####',
      '########..........######',
      '##########.......#######',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },

  // ---- Ts (open N+E+W) ---------------------------------------------------
  {
    id: 'hall-t',
    kind: 'hall',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '#######..........#######',
      '######............######',
      '######............######',
      '######............######',
      '#####...............####',
      '###..................###',
      '........................',
      '........................',
      '........................',
      '...........*............',
      '................##......',
      '................##......',
      '......e.................',
      '........................',
      '####..............######',
      '######..........########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    id: 'room-t',
    kind: 'room',
    size: 1,
    rows: [
      '########........########',
      '######............######',
      '#####..............#####',
      '####................####',
      '###..................###',
      '##....................##',
      '##....................##',
      '##.........e.........###',
      '........................',
      '........................',
      '........................',
      '............*...........',
      '........................',
      '........................',
      '........................',
      '......e.................',
      '###.................####',
      '#####.............######',
      '########........########',
      '########################',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },

  // ---- crosses (all edges open) ------------------------------------------
  {
    id: 'hall-cross',
    kind: 'hall',
    size: 1,
    rows: [
      '########........########',
      '#######..........#######',
      '#######..........#######',
      '######............######',
      '######............######',
      '#####..............#####',
      '####................####',
      '###..................###',
      '........................',
      '........................',
      '........................',
      '...........*............',
      '........................',
      '........................',
      '.................e......',
      '........................',
      '###..................###',
      '####................####',
      '#####..............#####',
      '######............######',
      '######............######',
      '#######..........#######',
      '#######..........#######',
      '########........########',
    ],
  },
  {
    id: 'room-cross',
    kind: 'room',
    size: 1,
    rows: [
      '########........########',
      '######............######',
      '####................####',
      '###..................###',
      '##....................##',
      '##....................##',
      '##.....e..............##',
      '#......................#',
      '........................',
      '........................',
      '........................',
      '...........*............',
      '........................',
      '.................##.....',
      '.....e...........##.....',
      '........................',
      '#......................#',
      '##...............e....##',
      '##....................##',
      '###..................###',
      '####................####',
      '#####..............#####',
      '######............######',
      '########........########',
    ],
  },

  // ---- grand (2×2 arena; all 8 segments open, assembler seals unused) ----
  {
    id: 'grand-cavern',
    kind: 'grand',
    size: 2,
    rows: [
      '########........################........########',
      '######....................................######',
      '#####......................................#####',
      '####........................................####',
      '###..........................................###',
      '##............................................##',
      '##............................................##',
      '##............................................##',
      '................................................',
      '................................................',
      '................................................',
      '................................................',
      '............##..................................',
      '............##..................................',
      '................................................',
      '................................................',
      '##............................................##',
      '#####.........................................##',
      '#####.........................................##',
      '#####.........................................##',
      '##..................................##........##',
      '##..................................##........##',
      '##............................................##',
      '##.....................*......................##',
      '##............................................##',
      '##............................................##',
      '##......##....................................##',
      '##......##....................................##',
      '##..........................................####',
      '##..........................................####',
      '##............................................##',
      '##............................................##',
      '................................................',
      '................................................',
      '..................................##............',
      '..................................##............',
      '................................................',
      '................................................',
      '................................................',
      '................................................',
      '##............................................##',
      '##............................................##',
      '##............................................##',
      '###..........................................###',
      '####........................................####',
      '#####......................................#####',
      '######....................................######',
      '########........################........########',
    ],
  },

  // ---- sanctum giant (3×3; the whole floor is one room) ------------------
  {
    id: 'grand-sanctum',
    kind: 'grand',
    size: 3,
    rows: [
      '########........################........################........########',
      '########........################........################........########',
      '#######..........................................................#######',
      '######............................................................######',
      '#####..............................................................#####',
      '####................................................................####',
      '###..................................................................###',
      '##....................................................................##',
      '........................................................................',
      '........................................................................',
      '........................................................................',
      '........................................................................',
      '..................................................##....................',
      '..................................................##....................',
      '.............###..................................##....................',
      '.............###........................................................',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##............................##......................................##',
      '##............................##......................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##.......................................................###..........##',
      '##.......................................................###..........##',
      '........................................................................',
      '..........................##............................................',
      '..........................##............................................',
      '........................................................................',
      '....................................*...................................',
      '........................................................................',
      '........................................................................',
      '........................................................................',
      '##................##..................................................##',
      '##................##..................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##..........................................###.......................##',
      '##..........................................###.......................##',
      '##..........................................###.......................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##....................................................................##',
      '##...............................##...................................##',
      '............##...................##.....................................',
      '............##..........................................................',
      '............................................................##..........',
      '............................................................##..........',
      '........................................................................',
      '........................................................................',
      '........................................................................',
      '........................................................................',
      '##....................................................................##',
      '###..................................................................###',
      '####................................................................####',
      '#####..............................................................#####',
      '######............................................................######',
      '#######..........................................................#######',
      '########........################........################........########',
      '########........################........################........########',
    ],
  },
];
